import type { PendingAuthActionType } from "@storytell/core";
import { type AuthGatingFeature, authGateStatus } from "~/config/authGating";
import { useWire } from "~/wire";

type AuthGateOptions<T extends Array<unknown> = Array<unknown>> = {
  title: string;
  description?: string;
  /** The type of action being gated - used for post-auth restoration */
  actionType?: PendingAuthActionType;
  /** Function to capture state data to restore after auth. Receives the action args. */
  captureState?: (...args: T) => unknown;
};

/**
 * Hook to gate an action by authentication status.
 *
 * @param feature - Feature key from authGatingConfig. If the feature is not gated
 *                  in the config, the action will be executed without auth check.
 *                  Pass `null` to always require auth (legacy behavior).
 * @param options - Function returning the modal options (title, description, etc.)
 * @param action - The action to execute if the user is authenticated
 * @param onCancel - Optional callback when the action is cancelled due to auth gate
 */
export const useGateActionByAuthenticated = <T extends Array<unknown>, U>(
  feature: AuthGatingFeature | null,
  options: () => AuthGateOptions<T>,
  action: (...args: T) => U,
  onCancel?: () => void
) => {
  const wire = useWire();

  const wrappedAction = (...args: T): U | undefined => {
    const status = authGateStatus(wire.auth, feature);

    // Not gated, or the user is registered: execute the action directly
    if (status === "allow") {
      return action(...args);
    }

    // Auth has not resolved yet: cancel rather than prompt a user who may be signed in
    if (status === "pending") {
      onCancel?.();
      return;
    }

    // Gated: the user is not registered (guest or any non-authenticated state)
    const opts = options();
    const pendingAction =
      opts.actionType != null
        ? {
            type: opts.actionType,
            data: opts.captureState?.(...args),
          }
        : undefined;
    wire.ui.authGate.openModal({
      title: opts.title,
      description: opts.description,
      pendingAction,
      feature: feature ?? undefined,
    });
    onCancel?.();
    return;
  };

  return wrappedAction;
};
